import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAdminTours, deleteTour, updateTour, createTour } from '../../services/api';
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, Star, Copy } from 'lucide-react';

const AdminTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(null);
  const navigate = useNavigate();

  const fetch = async () => {
    try {
      const res = await getAdminTours();
      setTours(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const toggleActive = async (tour) => {
    await updateTour(tour.id, { active: !tour.active });
    fetch();
  };

  const handleDelete = async (tour) => {
    if (!window.confirm(`"${tour.title}" silinecek. Emin misiniz?`)) return;
    await deleteTour(tour.id);
    fetch();
  };

  const handleDuplicate = async (tour) => {
    if (!window.confirm(`"${tour.title}" kopyalanacak. Devam etmek istiyor musunuz?`)) return;
    setDuplicating(tour.id);
    
    try {
      // Only copy the fields we need, exclude problematic ones
      const tourCopy = {
        title: `${tour.title} - Kopya`,
        slug: `${tour.slug}-kopya-${Date.now()}`,
        description: tour.description || '',
        location: tour.location || '',
        nights: tour.nights || 0,
        days: tour.days || 0,
        difficulty: tour.difficulty || '',
        level: tour.level || 1,
        levelName: tour.levelName || '',
        currency: tour.currency || '₺',
        originalPrice: tour.originalPrice || 0,
        salePrice: tour.salePrice || 0,
        onSale: tour.onSale || false,
        rating: tour.rating || 0,
        reviewCount: tour.reviewCount || 0,
        heroImage: tour.heroImage || '',
        images: tour.images || [],
        itinerary: tour.itinerary || [],
        included: tour.included || [],
        notIncluded: tour.notIncluded || [],
        highlights: tour.highlights || [],
        dates: tour.dates || [],
        pricingDates: tour.pricingDates || [],
        active: false, // Start as inactive
      };
      
      const res = await createTour(tourCopy);
      
      if (res.data && res.data.id) {
        alert('Tur başarıyla kopyalandı! Düzenleme sayfasına yönlendiriliyorsunuz...');
        navigate(`/admin/turlar/${res.data.id}/duzenle`);
      } else {
        throw new Error('Tur oluşturulamadı');
      }
    } catch (err) {
      console.error('Duplicate error:', err);
      alert('Kopyalama sırasında bir hata oluştu: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDuplicating(null);
      fetch();
    }
  };

  const formatPrice = (p) => p ? new Intl.NumberFormat('tr-TR').format(p) : '-';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3D4A26]" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">{tours.length} tur</p>
        <Link to="/admin/turlar/yeni"
          className="flex items-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-semibold px-4 py-2 rounded-lg hover:bg-[#b8c83a] transition-colors text-sm"
        >
          <Plus size={16} /> Yeni Tur
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Tur</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Konum</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Fiyat</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Seviye</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Puan</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Durum</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tours.map(tour => (
              <tr key={tour.id} className={`hover:bg-gray-50 transition-colors ${!tour.active ? 'opacity-50' : ''}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={tour.heroImage || ''} alt="" className="w-14 h-10 rounded-lg object-cover flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-sm text-[#1B1B1B] line-clamp-1">{tour.title}</p>
                      <p className="text-xs text-gray-500">{tour.duration}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-gray-600">{tour.location}</td>
                <td className="px-5 py-3 text-center">
                  <div>
                    <p className="text-sm font-semibold">{tour.currency}{formatPrice(tour.salePrice)}</p>
                    {tour.onSale && tour.originalPrice && <p className="text-xs text-gray-400 line-through">{tour.currency}{formatPrice(tour.originalPrice)}</p>}
                  </div>
                </td>
                <td className="px-5 py-3 text-center text-sm">{tour.level}</td>
                <td className="px-5 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Star size={12} fill="#C8D843" stroke="#C8D843" />
                    <span className="text-sm font-semibold">{tour.rating}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-center">
                  <button onClick={() => toggleActive(tour)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${tour.active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                    {tour.active ? 'Aktif' : 'Gizli'}
                  </button>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button 
                      onClick={() => handleDuplicate(tour)} 
                      disabled={duplicating === tour.id}
                      className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-50" 
                      title="Kopyala"
                    >
                      {duplicating === tour.id ? (
                        <Loader2 size={16} className="text-gray-400 animate-spin" />
                      ) : (
                        <Copy size={16} className="text-blue-500" />
                      )}
                    </button>
                    <button onClick={() => toggleActive(tour)} className="p-1.5 hover:bg-gray-100 rounded-lg" title={tour.active ? 'Gizle' : 'Aktif Et'}>
                      {tour.active ? <EyeOff size={16} className="text-gray-400" /> : <Eye size={16} className="text-green-500" />}
                    </button>
                    <Link to={`/admin/turlar/${tour.id}/duzenle`} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Düzenle">
                      <Pencil size={16} className="text-gray-400" />
                    </Link>
                    <button onClick={() => handleDelete(tour)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Sil">
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {tours.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-500">Henüz tur eklenmemiş</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTours;
